import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  Target, 
  BarChart3, 
  Zap, 
  Award,
  ArrowRight,
  CheckCircle,
  Users,
  TrendingUp,
  Shield,
  Clock,
  Brain,
  Eye
} from "lucide-react";

const WhyUs = () => {
  const differentiators = [
    {
      icon: <BarChart3 className="h-12 w-12" />,
      title: "Performance-First Mindset",
      description: "Every decision is backed by data. We focus on metrics that matter: ROAS, CPA, LTV, and conversion rates.",
      features: [
        "Data-driven decision making",
        "Real-time performance monitoring", 
        "ROI-focused optimization",
        "Transparent reporting"
      ]
    },
    {
      icon: <Target className="h-12 w-12" />,
      title: "Data & ROAS Focused Decisions",
      description: "We don't guess - we analyze. Our strategies are built on comprehensive data analysis and proven frameworks.",
      features: [
        "Advanced analytics setup",
        "Attribution modeling",
        "Predictive performance analysis",
        "Continuous optimization loops"
      ]
    },
    {
      icon: <Brain className="h-12 w-12" />,
      title: "Creative + AI Powered Execution",
      description: "The perfect blend of human creativity and AI efficiency for scalable, high-converting content.",
      features: [
        "Human creative strategy",
        "AI-powered content generation",
        "Rapid testing & iteration",
        "Scalable content production"
      ]
    },
    {
      icon: <Eye className="h-12 w-12" />,
      title: "Transparent Reporting & Scaling Systems",
      description: "Full visibility into your campaigns with clear reporting and systematic scaling processes.",
      features: [
        "Real-time dashboard access",
        "Weekly performance reports",
        "Clear scaling frameworks",
        "Direct communication channels"
      ]
    }
  ];

  const stats = [
    { number: "300%", label: "Average ROAS Increase", description: "Across all client accounts" },
    { number: "$50M+", label: "Revenue Generated", description: "For our clients in 2023" },
    { number: "500+", label: "Campaigns Optimized", description: "Across all platforms" },
    { number: "98%", label: "Client Retention Rate", description: "Long-term partnerships" }
  ];

  const testimonials = [
    {
      quote: "GrowthPro transformed our marketing. We went from struggling with 1.5x ROAS to consistently hitting 4-5x ROAS within 3 months.",
      author: "Sarah Chen",
      role: "CEO, EcoBeauty",
      company: "E-commerce Beauty Brand"
    },
    {
      quote: "Their AI-powered video creation saved us thousands while increasing our conversion rates by 180%. Game-changing partnership.",
      author: "Marcus Rodriguez", 
      role: "Marketing Director",
      company: "TechStart Solutions"
    },
    {
      quote: "Finally, an agency that actually understands performance marketing. Transparent, data-driven, and results-focused.",
      author: "Jennifer Walsh",
      role: "Founder",
      company: "Wellness Co."
    }
  ];

  const certifications = [
    "Google Ads Certified",
    "Meta Blueprint Certified", 
    "TikTok Marketing Partner",
    "Snapchat Ads Certified",
    "Google Analytics 4 Certified",
    "HubSpot Growth Marketing"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6">
              <Award className="h-3 w-3 mr-1" />
              Performance Marketing Experts
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Why Choose <span className="text-gradient">GrowthPro</span>?
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8">
              We're not just another marketing agency. We're your growth partners, 
              committed to delivering measurable results through data-driven strategies and innovative AI solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="pb-20 lg:pb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              What Makes Us <span className="text-gradient">Different</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our unique approach combines proven performance marketing strategies with cutting-edge AI technology.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {differentiators.map((item, index) => (
              <Card key={index} className="card-elegant group">
                <CardHeader className="pb-4">
                  <div className="text-primary group-hover:scale-110 transition-transform duration-300 mb-4">
                    {item.icon}
                  </div>
                  <CardTitle className="text-2xl mb-3">{item.title}</CardTitle>
                  <p className="text-muted-foreground">{item.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {item.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Proven <span className="text-gradient\">Results</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Numbers don't lie. Here's what we've achieved for our clients.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <Card key={index} className="card-elegant text-center">
                <CardContent className="p-8">
                  <div className="text-4xl lg:text-5xl font-bold text-gradient mb-4">
                    {stat.number}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{stat.label}</h3>
                  <p className="text-sm text-muted-foreground">{stat.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              What Our <span className="text-gradient\">Clients Say</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Real feedback from real businesses we've helped scale.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="card-elegant">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <div className="flex text-primary mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-lg italic mb-6">\"{testimonial.quote}\"</p>
                  </div>
                  <div>
                    <div className="font-semibold">{testimonial.author}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                    <div className="text-sm text-primary">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Certified <span className="text-gradient\">Experts</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Our team holds industry-leading certifications across all major platforms.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {certifications.map((cert, index) => (
              <Card key={index} className="card-elegant text-center">
                <CardContent className="p-6">
                  <Award className="h-8 w-8 text-primary mx-auto mb-3" />
                  <div className="text-sm font-medium">{cert}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="card-elegant bg-gradient-to-r from-primary/10 to-primary-glow/10 border-primary/20">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                Ready to <span className="text-gradient\">Partner With Us</span>?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join hundreds of successful brands who trust us to drive their growth with performance marketing excellence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="btn-glow text-lg px-8 py-6">
                  <Link to="/contact">
                    Start Your Growth Journey
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6">
                  <Link to="/case-studies">
                    See Our Case Studies
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default WhyUs;